import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { invProcessApi, FM005 } from '../../../services/invProcessApi';

export type Mode = 'browse' | 'add' | 'edit';

export function useInvProcessForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<FM005[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);

  // Form fields
  const [code, setCode] = useState('');
  const [desc, setDesc] = useState('');

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  // Load all records
  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await invProcessApi.list(1, 1000);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync form fields from current record
  useEffect(() => {
    if (current && mode === 'browse') {
      setCode(current.fm005_01);
      setDesc(current.fm005_11);
    }
  }, [current, mode]);

  // Initial load
  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  // Navigation
  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  // Start add mode
  const startAdd = () => {
    setMode('add');
    setCode('');
    setDesc('');
  };

  // Start edit mode
  const startEdit = () => {
    if (!current) return;
    setMode('edit');
    setCode(current.fm005_01);
    setDesc(current.fm005_11);
  };

  // Cancel (restore)
  const cancel = () => {
    setMode('browse');
    if (current) {
      setCode(current.fm005_01);
      setDesc(current.fm005_11);
    }
  };

  // Save
  const save = async () => {
    try {
      if (mode === 'add') {
        await invProcessApi.create({ fm005_01: code, fm005_11: desc });
        message.success('新增成功');
      } else {
        await invProcessApi.update(current!.fm005_01, { fm005_11: desc });
        message.success('修改成功');
      }
      const data = await loadRecords();
      const targetCode = mode === 'add' ? code : current!.fm005_01;
      const idx = data.findIndex(r => r.fm005_01 === targetCode);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  // Delete
  const doDelete = async () => {
    if (!current) return;
    try {
      await invProcessApi.delete(current.fm005_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch {
      message.error('刪除失敗');
    }
  };

  // Quick search with "not found" feedback
  const quickSearch = (searchText: string) => {
    if (isEditing) return;
    // Exact match first
    const exact = records.findIndex(r => r.fm005_01 === searchText);
    if (exact >= 0) {
      setCurrentIdx(exact);
      return;
    }
    // Near match (first record >= searchText)
    const near = records.findIndex(r => r.fm005_01 >= searchText);
    if (near >= 0) {
      setCurrentIdx(near);
      message.info(`查無「${searchText}」，顯示最接近的記錄「${records[near].fm005_01}」`);
    } else {
      message.warning(`查無此代號「${searchText}」`);
    }
  };

  return {
    mode, isEditing, loading,
    records, current, currentIdx,
    code, setCode, desc, setDesc,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
    loadRecords,
  };
}
